import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Breadcrumb } from "../Breadcrumb";
import { useNavigate } from "react-router-dom";
import { PATHS } from "../config/paths";
import API from "../api/api01";
import { toast } from "sonner";
import { Loader2, Plus, ArrowLeft } from "lucide-react";

export function AddBook() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    author: "",
    isbn: "",
    category: "",
    copies: "1",
    description: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title || !formData.author) {
      toast.error("Title and Author are required fields");
      return;
    }

    setSubmitting(true);

    try {
      await API.post("/superadmin/library/books", {
        title: formData.title,
        author: formData.author,
        isbn: formData.isbn,
        category: formData.category,
        total_copies: parseInt(formData.copies),
        available_copies: parseInt(formData.copies),
        description: formData.description
      });
      
      toast.success("Book added successfully! Redirecting...");
      
      // Explicitly redirecting to DASHBOARD as requested by the user
      setTimeout(() => {
        navigate(PATHS.DASHBOARD);
      }, 1000);
    } catch (error) {
      toast.error("An error occurred while adding the book");
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white leading-none">Add New Book</h1>
            <Breadcrumb 
              items={[
                { label: "Library", path: PATHS.VIEW_LIBRARY },
                { label: "Add Book" }
              ]} 
              className="mt-2" 
            />
          </div>

          <Button 
            variant="outline" 
            onClick={() => navigate(PATHS.VIEW_LIBRARY)}
            className="rounded-xl border-white/10 hover:bg-white/5 h-10 px-4 text-xs font-bold uppercase tracking-widest transition-all active:scale-95"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Catalog
          </Button>
        </div>

        {/* Form Card */}
        <Card className="bg-card border-white/10 shadow-xl rounded-2xl overflow-hidden">
          <CardHeader className="border-b border-white/5 bg-white/[0.02] p-6">
            <CardTitle className="text-xs font-bold uppercase tracking-widest text-muted-foreground/80">Book Details</CardTitle>
            <p className="text-[13px] font-medium text-slate-500 dark:text-slate-400 mt-1">
              Enter the publication details of the new book to add it to the library catalog.
            </p>
          </CardHeader>
          
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                
                {/* Title */}
                <div className="space-y-2">
                  <Label htmlFor="title" className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80 ml-1">
                    Book Title <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="e.g. Clean Code"
                    className="h-11 bg-background/50 border-white/10 focus:border-blue-500/50 transition-colors px-4 rounded-xl"
                  />
                </div>

                {/* Author */}
                <div className="space-y-2">
                  <Label htmlFor="author" className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80 ml-1">
                    Author Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="author"
                    name="author"
                    value={formData.author}
                    onChange={handleChange}
                    placeholder="e.g. Robert C. Martin"
                    className="h-11 bg-background/50 border-white/10 focus:border-blue-500/50 transition-colors px-4 rounded-xl"
                  />
                </div>

                {/* ISBN */}
                <div className="space-y-2">
                  <Label htmlFor="isbn" className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80 ml-1">
                    ISBN Number
                  </Label>
                  <Input
                    id="isbn"
                    name="isbn"
                    value={formData.isbn}
                    onChange={handleChange}
                    placeholder="978-XXXXXXXXXX"
                    className="h-11 bg-background/50 border-white/10 focus:border-blue-500/50 transition-colors px-4 rounded-xl font-mono"
                  />
                </div>

                {/* Category */}
                <div className="space-y-2">
                  <Label htmlFor="category" className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80 ml-1">
                    Category / Genre
                  </Label>
                  <Input
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    placeholder="e.g. Software Engineering"
                    className="h-11 bg-background/50 border-white/10 focus:border-blue-500/50 transition-colors px-4 rounded-xl"
                  />
                </div>

                {/* Copies */}
                <div className="space-y-2">
                  <Label htmlFor="copies" className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80 ml-1">
                    Number of Copies
                  </Label>
                  <Input
                    id="copies"
                    name="copies"
                    type="number"
                    min="1"
                    value={formData.copies}
                    onChange={handleChange}
                    className="h-11 bg-background/50 border-white/10 focus:border-blue-500/50 transition-colors px-4 rounded-xl font-bold"
                  />
                </div>

                {/* Description */}
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="description" className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80 ml-1">
                    Brief Description
                  </Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Enter a short summary of the book content..."
                    rows={4}
                    className="bg-background/50 border-white/10 focus:border-blue-500/50 transition-colors p-4 rounded-xl resize-none"
                  />
                </div>
              </div>

              {/* Submit Section */}
              <div className="pt-6 border-t border-white/5 flex items-center justify-between">
                <div className="flex items-center gap-2 text-[11px] font-medium text-muted-foreground bg-white/5 px-4 py-2 rounded-full">
                  <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                  Live API Connection Active
                </div>

                <div className="flex gap-3">
                  <Button 
                    type="button" 
                    variant="ghost" 
                    onClick={() => navigate(PATHS.VIEW_LIBRARY)}
                    className="h-11 px-8 rounded-xl font-bold uppercase tracking-widest text-xs hover:bg-white/5 transition-all"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={submitting} 
                    className="h-11 px-10 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold uppercase tracking-widest text-xs shadow-lg shadow-blue-500/20 shadow-lg transition-all active:scale-95"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Adding Book...
                      </>
                    ) : (
                      <>
                        <Plus className="mr-2 h-4 w-4" />
                        Add Book to Catalog
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
